import { useState } from "react";
import { toast } from "sonner";
import { X, Lock } from "lucide-react";
import { formatMoney } from "@/lib/money";

const UNITS = ["Per Day", "Per Head", "Per Shoot", "Per Hour"];
const GROUPS = ["Crew", "Equipment", "Services"];

function DrawerShell({ kicker, title, onClose, children, footer, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid={testId}>
      <div className="absolute inset-0" style={{ background: "var(--pw-scrim)" }} onClick={onClose} />
      <div className="relative w-full max-w-[460px] h-full bg-[color:var(--pw-surface)] flex flex-col pw-enter" style={{ boxShadow: "var(--pw-shadow-drawer)" }}>
        <div className="flex items-start justify-between px-6 py-5 border-b" style={{ borderColor: "var(--pw-divider)" }}>
          <div>
            <div className="pw-eyebrow mb-2">{kicker}</div>
            <div className="pw-display text-[20px]">{title}</div>
          </div>
          <button onClick={onClose} data-testid="rate-card-drawer-close" className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[color:var(--pw-warm-tint)]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t flex items-center justify-between sticky bottom-0 bg-[color:var(--pw-surface)]" style={{ borderColor: "var(--pw-divider)" }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function LockBanner({ text }) {
  return (
    <div className="rounded-xl p-4 mb-5 flex items-start gap-3" style={{ background: "var(--pw-warm-tint)" }}>
      <Lock className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--pw-text-secondary)" }} />
      <p className="text-[13.5px]" style={{ color: "var(--pw-text-secondary)" }}>{text}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="text-[12.5px] font-bold block mb-1.5" style={{ color: "var(--pw-text-primary)" }}>{label}</label>
      {children}
    </div>
  );
}

function RequestChangeForm({ item, currency, onClose, onSubmit }) {
  const [proposedRate, setProposedRate] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!proposedRate) { toast.error("Enter a proposed rate."); return; }
    setBusy(true);
    try {
      await onSubmit(item.canonical_id, { proposed_rate: Number(proposedRate), effective_from: effectiveFrom || null, reason: reason || null });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.code === "item_not_found" ? "This item couldn't be found." : "Couldn't submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DrawerShell kicker="Request Rate Change" title={item.service_name} onClose={onClose} testId="rate-card-drawer-change"
      footer={<>
        <button onClick={onClose} className="pw-btn-secondary px-4 py-2.5 text-[13px]" data-testid="rate-card-drawer-cancel">Cancel</button>
        <button onClick={submit} disabled={busy} className="pw-btn-primary px-4 py-2.5 text-[13px]" data-testid="rate-card-drawer-submit-change">Submit for NJM Approval</button>
      </>}>
      <LockBanner text="Approved rates are locked and remain operational until NJM approves a change. You can propose changes — NJM reviews and confirms before any rate becomes active." />
      <Field label="Current rate">
        <div className="rounded-[10px] px-3.5 py-2.5 font-bold text-[14px]" style={{ background: "#F4F2EC", border: "1px solid #E2DFD5" }}>{formatMoney(item.rate, currency)}</div>
      </Field>
      <Field label="Proposed rate">
        <input type="number" value={proposedRate} onChange={(e) => setProposedRate(e.target.value)} placeholder="Enter proposed rate" data-testid="rate-card-input-proposed-rate"
          className="w-full rounded-[10px] px-3.5 py-2.5 text-[14px] font-bold outline-none" style={{ border: "1px solid var(--pw-yellow)", background: "#FFFDF6", color: "var(--pw-pending-text)" }} />
      </Field>
      <Field label="Effective from">
        <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} data-testid="rate-card-input-effective-from" className="w-full pw-input px-3.5 py-2.5 text-[14px]" />
      </Field>
      <Field label="Reason for change">
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain why this rate needs to change" data-testid="rate-card-input-reason"
          className="w-full pw-input px-3.5 py-2.5 text-[14px] min-h-[100px]" />
      </Field>
    </DrawerShell>
  );
}

function ProposeNewForm({ onClose, onSubmit }) {
  const [group, setGroup] = useState("Crew");
  const [unit, setUnit] = useState(UNITS[0]);
  const [itemName, setItemName] = useState("");
  const [proposedRate, setProposedRate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!itemName || !proposedRate) { toast.error("Item name and proposed rate are required."); return; }
    setBusy(true);
    try {
      await onSubmit({ group, unit, item_name: itemName, proposed_rate: Number(proposedRate), notes: notes || null });
    } catch {
      toast.error("Couldn't submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DrawerShell kicker="New Rate" title="Propose New Rate" onClose={onClose} testId="rate-card-drawer-new"
      footer={<>
        <button onClick={onClose} className="pw-btn-secondary px-4 py-2.5 text-[13px]" data-testid="rate-card-drawer-cancel">Cancel</button>
        <button onClick={submit} disabled={busy} className="pw-btn-primary px-4 py-2.5 text-[13px]" data-testid="rate-card-drawer-submit-new">Submit to NJM</button>
      </>}>
      <LockBanner text="New rates are not operational until NJM confirms. They will be visible under 'Pending NJM Approval'." />
      <Field label="Group">
        <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid="rate-card-input-group" className="w-full pw-input px-3.5 py-2.5 text-[14px]">
          {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </Field>
      <Field label="Unit">
        <select value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="rate-card-input-unit" className="w-full pw-input px-3.5 py-2.5 text-[14px]">
          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </Field>
      <Field label="Item">
        <input value={itemName} onChange={(e) => setItemName(e.target.value)} placeholder="e.g., Sony FX6" data-testid="rate-card-input-item-name" className="w-full pw-input px-3.5 py-2.5 text-[14px]" />
      </Field>
      <Field label="Proposed rate">
        <input type="number" value={proposedRate} onChange={(e) => setProposedRate(e.target.value)} placeholder="Enter rate" data-testid="rate-card-input-new-rate" className="w-full pw-input px-3.5 py-2.5 text-[14px]" />
      </Field>
      <Field label="Notes">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes for NJM" data-testid="rate-card-input-notes" className="w-full pw-input px-3.5 py-2.5 text-[14px] min-h-[100px]" />
      </Field>
    </DrawerShell>
  );
}

function ViewPendingPanel({ item, currency, onClose }) {
  return (
    <DrawerShell kicker="Pending Proposal" title={item.service_name} onClose={onClose} testId="rate-card-drawer-view"
      footer={<button onClick={onClose} className="pw-btn-primary px-4 py-2.5 text-[13px] w-full" data-testid="rate-card-drawer-close-view">Close</button>}>
      <Field label="Current rate">
        <div className="text-[14px] font-medium">{formatMoney(item.rate, currency)}</div>
      </Field>
      <Field label="Proposed rate">
        <div className="text-[14px] font-bold" style={{ color: "var(--pw-pending-text)" }}>{formatMoney(item.proposed_rate, currency)}</div>
      </Field>
      <Field label="Reason for change">
        <div className="text-[14px]" style={{ color: "var(--pw-text-secondary)" }}>{item.reason || item.notes || "—"}</div>
      </Field>
      <Field label="Status">
        <span className="pw-pill" style={{ background: "var(--pw-pending-bg)", color: "var(--pw-pending-text)" }}>Pending NJM Approval</span>
      </Field>
    </DrawerShell>
  );
}

export function RateCardDrawer({ drawer, currency, onClose, onSubmitChange, onSubmitNew }) {
  if (drawer.mode === "change") return <RequestChangeForm item={drawer.item} currency={currency} onClose={onClose} onSubmit={onSubmitChange} />;
  if (drawer.mode === "new") return <ProposeNewForm onClose={onClose} onSubmit={onSubmitNew} />;
  if (drawer.mode === "view") return <ViewPendingPanel item={drawer.item} currency={currency} onClose={onClose} />;
  return null;
}
